import clsx from 'clsx';
import { ArrowUpRight } from 'lucide-react';
import type { SalesRep } from '../ProjectManagement/team';
import { formatDayMonth, formatDayMonthYear, formatTime } from '../../utils/dateFormat';

/**
 * A neutral initials avatar for a rep. Colour is reserved for status, so every
 * rep gets the same sunken grey disc; the name is on hover.
 */
export function RepInitials({ rep, className }: { rep: SalesRep | null | undefined; className?: string }) {
  return (
    <span
      className={clsx(
        'inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-surface-sunken font-label text-[10px] text-text-secondary',
        className
      )}
      title={rep?.name ?? 'Unassigned'}
    >
      {rep?.initials ?? '?'}
    </span>
  );
}

/**
 * Attio deal names are often slugs typed by hand ("Orange_Initial Roll out",
 * "acme__renewal  2026"). Shown to humans, underscores become " · " and runs of
 * whitespace collapse. Display only — the record keeps its real name.
 */
export function humanizeDealName(name: string | null | undefined): string {
  if (!name) return '';
  return name
    .replace(/_+/g, ' · ')
    .replace(/\s*·\s*/g, ' · ')
    .replace(/\s+/g, ' ')
    .replace(/^ · | · $/g, '')
    // "Roll out", "rollout", "Roll-Out" all read as one term.
    .replace(/\broll[\s-]?out\b/gi, 'Roll-out')
    .trim();
}

/**
 * The company sub-line under a deal name, or null when the humanised name
 * already starts with the company ("Aruba · Roll-out" under Aruba).
 */
export function dealCompanyLine(dealName: string | null | undefined, companyName: string | null | undefined): string | null {
  if (!companyName) return null;
  const name = humanizeDealName(dealName).toLowerCase();
  const company = companyName.trim().toLowerCase();
  if (!company || name.startsWith(company)) return null;
  // "HENSOLDT · Tender · 7" under HENSOLDT, "Renewal ISS USA" under ISS Facility Services: the deal
  // name already carries the company's first word, so the sub-line would only repeat it.
  const firstWord = company.split(/[\s,.()-]+/).find((w) => w.length >= 3);
  const words = new Set(name.split(/[\s·_,.()-]+/).filter(Boolean));
  return firstWord && words.has(firstWord) ? null : companyName;
}

const sameYear = (d: Date) => d.getFullYear() === new Date().getFullYear();

/**
 * "7 Oct" this year, "7 Oct 2025" any other year — a date without its year is
 * only unambiguous inside the current one. Accepts an ISO date or timestamp.
 */
export function formatShortDate(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(/^\d{4}-\d{2}-\d{2}$/.test(input) ? `${input}T12:00:00` : input);
  if (Number.isNaN(d.getTime())) return '';
  return sameYear(d) ? formatDayMonth(d) : formatDayMonthYear(d);
}

/** "7 Oct, 14:30" this year, "7 Oct 2025, 14:30" any other year. */
export function formatShortDateTime(input: string | Date | null | undefined): string {
  const date = formatShortDate(input);
  return date ? `${date}, ${formatTime(input!)}` : '';
}

/** "adecco-group" → "Adecco Group"; an all-lowercase stem only, so "IBM" or "eBay" typed as a name is left alone. */
function titleCaseStem(stem: string): string {
  if (stem !== stem.toLowerCase()) return stem;
  return stem
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const DOMAIN_LIKE = /^(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)*)\.[a-z]{2,}(?:\/.*)?$/i;

/**
 * One company label when a source mixes names and domains: the name when there
 * is one; otherwise the domain stem without "www." and TLD noise, title-cased so
 * it reads as a name ("kapsch.net" → "Kapsch", "acme.co.uk" → "Acme",
 * "adeccogroup.com" → "Adeccogroup"). The raw value belongs in a title.
 */
/**
 * One rule for the grey domain line under a company, everywhere in Sales and
 * CSM: the bare domain, lower-case, no scheme, no "www.", no path
 * ("https://www.UK.issworld.com/x" → "uk.issworld.com"). Empty when there is none.
 */
export function bareDomain(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '');
}

export function companyLabel(name: string | null | undefined, domain?: string | null): string {
  const n = name?.trim();
  if (n && !DOMAIN_LIKE.test(n)) return n;
  const raw = (n || domain || '').trim();
  const m = DOMAIN_LIKE.exec(raw);
  if (!m) return raw;
  // Keep the registrable label: the first label of what's left once the TLD is gone.
  const labels = m[1]!.split('.');
  const label = labels.length > 1 && /^(co|com|org|net|gov|ac)$/i.test(labels[labels.length - 1]!) ? labels[labels.length - 2]! : labels[labels.length - 1]!;
  return titleCaseStem(label);
}

/**
 * The owner who covers most rows of a list (at least `share` of them, and more
 * than one row), or null. That owner is named once in the header and the rows
 * only show the other owners' initials, instead of "PD" down a long run.
 */
export function majorityOwner<T>(rows: readonly T[], pick: (row: T) => string | null | undefined, share = 0.6): string | null {
  if (rows.length < 3) return null;
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = pick(row);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const [top] = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  return top && top[1] / rows.length >= share ? top[0] : null;
}

/** Row-end link out to an Attio record (deal, company or person). Renders nothing without a URL. */
export function AttioLink({ href, label }: { href: string | null | undefined; label: string }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Open in Attio"
      aria-label={`Open ${label} in Attio`}
      className="inline-flex h-7 w-7 flex-none items-center justify-center rounded text-text-muted hover:bg-surface-secondary hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-text-primary"
    >
      <ArrowUpRight className="h-4 w-4" aria-hidden />
    </a>
  );
}
